import React, { useContext, useEffect } from 'react';
import { HSOptions } from '../../../constants/HSOptions';
import Select from '../Select';
import { FormContext } from '../Form';
import Row from '../../layout/Row';
import styled from 'styled-components';

/**
 * UpgradeAndAutoSelect
 *
 * Props:
 * @param {boolean} [isAward]
 *        Hides "Award Default" [useDefault] option from Allow Upgrades
 * 
 * @param {string} [allowUpgradesFieldName="allowUpgrades"]
 *        Form field name for Allow Upgrades (Defaults to "allowUpgrades" if not provided)
 *
 * @param {string} [autoSelectFieldName="autoSelect"]
 *        Form field name for Auto Select (Defaults to "autoSelectItem" if not provided)
 */

const UpgradeAndAutoSelect = ({ isAward, allowUpgradesFieldName, autoSelectFieldName }) => {
    
    const { values, setValues } = useContext(FormContext);

    const ugsFieldName = allowUpgradesFieldName || 'allowUpgrades';
    const asFieldName = autoSelectFieldName || 'autoSelectItem';

    const normalize = (val) => {
        if (val === true || val === 'true') return 'True';
        if (val === false || val === 'false') return 'False';
        if (val == null) return 'UseDefault';
        return String(val);
    };

    const rawAllow = values[ugsFieldName];
    const rawAuto = values[asFieldName];

    useEffect(() => {
        const missingAllow = rawAllow === undefined || rawAllow === null;
        const missingAuto = rawAuto === undefined || rawAuto === null;

        if (!missingAllow && !missingAuto) return;

        const defaultAllow = isAward ? 'KioskDefault' : 'UseDefault';
        const defaultAuto = isAward ? 'KioskDefault' : 'UseDefault';

        setValues((v) => ({
            ...v,
            [ugsFieldName]: missingAllow ? defaultAllow : v[ugsFieldName],
            [asFieldName]: missingAuto ? defaultAuto : v[asFieldName],
        }));
    }, [rawAllow, rawAuto, isAward, ugsFieldName, asFieldName, setValues]);

    const allowUpgradesValue = normalize(rawAllow);
    const autoSelectValue = normalize(rawAuto);

    const forceMode = (() => {
        if (allowUpgradesValue === 'UseDefault' || allowUpgradesValue === 'KioskDefault') {
            return 'default';
        }
        if (allowUpgradesValue === 'False') {
            return 'no';
        }
        return 'free';
    })();

    const forceAutoSelect = forceMode !== 'free';

    const forcedValue = (() => {
        switch (forceMode) {
            case 'default':
                return allowUpgradesValue;
            case 'no':
                return 'True';
            default:
                return autoSelectValue;
        }
    })();

    const allowUpgradeOptions = isAward
        ? HSOptions.washClub.allowUpgrades.filter((o) => o.value !== 'UseDefault')
        : HSOptions.washClub.allowUpgrades;

    const autoSelectOptions = (() => {
        const base = isAward
            ? HSOptions.washClub.autoSelect.filter(o => o.value !== 'UseDefault')
            : HSOptions.washClub.autoSelect;

        switch (allowUpgradesValue) {
            case 'True':
                return base.filter(o => o.value === 'True' || o.value === 'False');
            case 'False':
                return base.filter(o => o.value === 'True');
            default:
                return base;
        }
    })();

    useEffect(() => {
        const current = values[ugsFieldName];

        if (isAward && current === 'UseDefault') {
            setValues(v => ({
                ...v,
                [ugsFieldName]: 'KioskDefault'
            }));
            return;
        }

        if (current == null) {
            setValues(v => ({
                ...v,
                [ugsFieldName]: isAward ? 'KioskDefault' : 'UseDefault'
            }));
        }
    }, [values, ugsFieldName, isAward, setValues]);

    useEffect(() => {
        if (!forceAutoSelect) return;

        const current = values[asFieldName];
        if (current !== forcedValue) {
            setValues(v => ({
                ...v,
                [asFieldName]: forcedValue
            }));
        }
    }, [forceAutoSelect, forcedValue, values, asFieldName, setValues]);

    useEffect(() => {
        if (forceAutoSelect) return;

        const current = values[asFieldName];
        if (current !== 'True' && current !== 'False') {
            setValues(v => ({
                ...v,
                [asFieldName]: 'True'
            }));
        }
    }, [forceAutoSelect, values, asFieldName, setValues]);

    if (rawAllow === undefined || rawAuto === undefined) {
        return null;
    }

    return (
        <Row wrap>
            <Select
                label="Allow Upgrades"
                name={ugsFieldName}
                options={allowUpgradeOptions}
                value={allowUpgradesValue}
                required
            />

            <Select
                label="Auto Select"
                name={asFieldName}
                options={autoSelectOptions}
                value={forcedValue}
                required
                readOnly={forceAutoSelect}
            />
        </Row>
    );
};

export default UpgradeAndAutoSelect;
